/**
 * Settings Dialog Component
 *
 * Application settings for auto-save, storage management, and data persistence.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '@/onboarding';
import { useFileStore } from '../fileStore';
import { modelStorage } from '../modelStorage';
import {
  getSimulationHistoryStats,
  clearSimulationHistory,
} from '@/simulation/simulationHistoryCache';
import {
  getCacheInfo,
  clearCache as clearWeatherCache,
  clearSearchHistory,
  clearRecentFiles as clearWeatherRecentFiles,
} from '@/weather/services/weatherFileCache';

interface StorageStats {
  models: { count: number; estimatedSize: number };
  simulationHistory: { count: number; successful: number; failed: number };
  weatherCache: { fileCount: number; totalSize: number };
  isLoading: boolean;
}

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const { autoSave, setAutoSaveEnabled, savedModels, refreshSavedModels, clearRecentFiles } =
    useFileStore();

  const [storageStats, setStorageStats] = useState<StorageStats>({
    models: { count: 0, estimatedSize: 0 },
    simulationHistory: { count: 0, successful: 0, failed: 0 },
    weatherCache: { fileCount: 0, totalSize: 0 },
    isLoading: true,
  });

  const [clearConfirm, setClearConfirm] = useState<string | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // Load storage stats when dialog opens
  useEffect(() => {
    if (!open) return;

    async function loadStats() {
      setStorageStats((prev) => ({ ...prev, isLoading: true }));

      try {
        // Load saved models
        await refreshSavedModels();

        // Load simulation history stats
        const simStats = await getSimulationHistoryStats();

        // Load weather cache info
        const weatherInfo = await getCacheInfo();

        // Estimate model storage size
        const modelSize = savedModels.reduce((sum, m) => sum + (m.content?.length ?? 0), 0);

        setStorageStats({
          models: { count: savedModels.length, estimatedSize: modelSize },
          simulationHistory: {
            count: simStats.totalSimulations,
            successful: simStats.successfulSimulations,
            failed: simStats.failedSimulations,
          },
          weatherCache: {
            fileCount: weatherInfo.files.length,
            totalSize: weatherInfo.totalSize,
          },
          isLoading: false,
        });
      } catch (error) {
        console.error('Failed to load storage stats:', error);
        setStorageStats((prev) => ({ ...prev, isLoading: false }));
      }
    }

    void loadStats();
  }, [open, refreshSavedModels, savedModels.length]);

  const formatBytes = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }, []);

  const handleClearModels = useCallback(async () => {
    setIsClearing(true);
    try {
      // Delete all saved models
      for (const model of savedModels) {
        await modelStorage.deleteModel(model.id);
      }
      await refreshSavedModels();
      clearRecentFiles();
      setClearConfirm(null);
    } catch (error) {
      console.error('Failed to clear models:', error);
    } finally {
      setIsClearing(false);
    }
  }, [savedModels, refreshSavedModels, clearRecentFiles]);

  const handleClearSimulationHistory = useCallback(async () => {
    setIsClearing(true);
    try {
      await clearSimulationHistory();
      // Refresh stats
      const simStats = await getSimulationHistoryStats();
      setStorageStats((prev) => ({
        ...prev,
        simulationHistory: {
          count: simStats.totalSimulations,
          successful: simStats.successfulSimulations,
          failed: simStats.failedSimulations,
        },
      }));
      setClearConfirm(null);
    } catch (error) {
      console.error('Failed to clear simulation history:', error);
    } finally {
      setIsClearing(false);
    }
  }, []);

  const handleClearWeatherCache = useCallback(async () => {
    setIsClearing(true);
    try {
      await clearWeatherCache();
      await clearSearchHistory();
      await clearWeatherRecentFiles();
      // Refresh stats
      const weatherInfo = await getCacheInfo();
      setStorageStats((prev) => ({
        ...prev,
        weatherCache: {
          fileCount: weatherInfo.files.length,
          totalSize: weatherInfo.totalSize,
        },
      }));
      setClearConfirm(null);
    } catch (error) {
      console.error('Failed to clear weather cache:', error);
    } finally {
      setIsClearing(false);
    }
  }, []);

  const handleClearAllData = useCallback(async () => {
    setIsClearing(true);
    try {
      await handleClearModels();
      await handleClearSimulationHistory();
      await handleClearWeatherCache();
      setClearConfirm(null);
    } catch (error) {
      console.error('Failed to clear all data:', error);
    } finally {
      setIsClearing(false);
    }
  }, [handleClearModels, handleClearSimulationHistory, handleClearWeatherCache]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 id="settings-title" className="text-lg font-semibold">
            Settings
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close settings"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {/* Auto-save Section */}
          <section className="mb-6">
            <h3 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Auto-save
            </h3>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium">Enable auto-save</p>
                <p className="text-sm text-muted-foreground">
                  Automatically save changes every 30 seconds
                </p>
              </div>
              <button
                role="switch"
                aria-checked={autoSave.enabled}
                onClick={() => setAutoSaveEnabled(!autoSave.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  autoSave.enabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background shadow-sm transition-transform ${
                    autoSave.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {autoSave.lastSaved && (
              <p className="mt-2 text-xs text-muted-foreground">
                Last auto-saved: {new Date(autoSave.lastSaved).toLocaleTimeString()}
              </p>
            )}
          </section>

          {/* Onboarding Section */}
          <section className="mb-6">
            <h3 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Onboarding
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium text-sm">Welcome Screen</p>
                  <p className="text-xs text-muted-foreground">Show the welcome introduction again</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    useOnboardingStore.getState().openWelcome();
                    onOpenChange(false);
                  }}
                >
                  Show
                </Button>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium text-sm">Reset Onboarding</p>
                  <p className="text-xs text-muted-foreground">
                    Clear all onboarding progress and hints
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    useOnboardingStore.getState().resetOnboarding();
                    onOpenChange(false);
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </section>

          {/* Storage Management Section */}
          <section>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Storage Management
            </h3>

            {storageStats.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Saved Models */}
                <StorageItem
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  }
                  title="Saved Models"
                  description={`${storageStats.models.count} models`}
                  size={formatBytes(storageStats.models.estimatedSize)}
                  onClear={() => setClearConfirm('models')}
                  showClearConfirm={clearConfirm === 'models'}
                  onConfirmClear={handleClearModels}
                  onCancelClear={() => setClearConfirm(null)}
                  isClearing={isClearing}
                  disabled={storageStats.models.count === 0}
                />

                {/* Simulation History */}
                <StorageItem
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  }
                  title="Simulation History"
                  description={`${storageStats.simulationHistory.count} runs (${storageStats.simulationHistory.successful} successful)`}
                  onClear={() => setClearConfirm('simulation')}
                  showClearConfirm={clearConfirm === 'simulation'}
                  onConfirmClear={handleClearSimulationHistory}
                  onCancelClear={() => setClearConfirm(null)}
                  isClearing={isClearing}
                  disabled={storageStats.simulationHistory.count === 0}
                />

                {/* Weather Cache */}
                <StorageItem
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2v2" />
                      <path d="m4.93 4.93 1.41 1.41" />
                      <path d="M20 12h2" />
                      <path d="m19.07 4.93-1.41 1.41" />
                      <path d="M15.947 12.65a4 4 0 0 0-5.925-4.128" />
                      <path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z" />
                    </svg>
                  }
                  title="Weather File Cache"
                  description={`${storageStats.weatherCache.fileCount} files cached`}
                  size={formatBytes(storageStats.weatherCache.totalSize)}
                  onClear={() => setClearConfirm('weather')}
                  showClearConfirm={clearConfirm === 'weather'}
                  onConfirmClear={handleClearWeatherCache}
                  onCancelClear={() => setClearConfirm(null)}
                  isClearing={isClearing}
                  disabled={storageStats.weatherCache.fileCount === 0}
                />

                {/* Clear All */}
                <div className="mt-4 pt-4 border-t">
                  {clearConfirm === 'all' ? (
                    <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
                      <p className="text-sm text-red-700 dark:text-red-400">
                        Delete all stored data? This cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setClearConfirm(null)}
                          disabled={isClearing}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-100 hover:text-red-700"
                          onClick={handleClearAllData}
                          disabled={isClearing}
                        >
                          {isClearing ? 'Clearing...' : 'Delete All'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
                      onClick={() => setClearConfirm('all')}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                      Clear All Data
                    </Button>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4">
          <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

interface StorageItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  size?: string;
  onClear: () => void;
  showClearConfirm: boolean;
  onConfirmClear: () => void;
  onCancelClear: () => void;
  isClearing: boolean;
  disabled?: boolean;
}

function StorageItem({
  icon,
  title,
  description,
  size,
  onClear,
  showClearConfirm,
  onConfirmClear,
  onCancelClear,
  isClearing,
  disabled,
}: StorageItemProps) {
  if (showClearConfirm) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950">
        <p className="text-sm text-red-700 dark:text-red-400">Clear {title.toLowerCase()}?</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancelClear} disabled={isClearing}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:bg-red-100 hover:text-red-700"
            onClick={onConfirmClear}
            disabled={isClearing}
          >
            {isClearing ? 'Clearing...' : 'Clear'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <span className="text-muted-foreground">{icon}</span>
        <div>
          <p className="font-medium text-sm">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {size && <span className="text-sm text-muted-foreground">{size}</span>}
        <Button variant="outline" size="sm" onClick={onClear} disabled={disabled}>
          Clear
        </Button>
      </div>
    </div>
  );
}
